import Dexie from 'dexie';
import DB, { RECENT_EMOJI_MAX } from "./db.js";
import dataCrypto from "../api/dataCrypto.js";
import { rewriteOriginUrls } from "../api/originRewrite.js";

const DB_NAME_PREFIX = 'im-web-';

class ImIndexedDB extends DB {
	constructor() {
		super();
		this.db = null;
	}

	open(userId) {
		super.open(userId);
		this.db = new Dexie(DB_NAME_PREFIX + userId);
		this.db.version(4).stores({
			config: 'key',
			conversations: 'key',
			messages: 'localId,id,[convKey+seqNo+sendTime]',
			friends: 'id',
			groups: 'id'
		});
		this.db.version(5).stores({
			config: 'key',
			conversations: 'key',
			messages: 'localId,id,[convKey+seqNo+sendTime]',
			friends: 'id',
			groups: 'id',
			recentEmojis: 'text,usedAt'
		});
	}

	close() {
		this.db && this.db.close();
	}

	// ===== 本地缓存加密：仅加密敏感正文字段，索引字段保持明文以支持查询 =====
	async _encConv(conv) {
		if (!conv) return conv;
		const c = { ...conv };
		if (typeof c.lastContent === 'string') {
			c.lastContent = await dataCrypto.enc(c.lastContent);
		}
		return c;
	}
	async _decConv(conv) {
		if (!conv) return conv;
		if (typeof conv.lastContent === 'string') {
			conv.lastContent = await dataCrypto.dec(conv.lastContent);
		}
		// 旧缓存里的绝对域名URL改写为相对路径(纯IP应急通道可用)
		return rewriteOriginUrls(conv);
	}
	async _encMsg(msg) {
		if (!msg) return msg;
		const m = { ...msg };
		if (typeof m.content === 'string') {
			m.content = await dataCrypto.enc(m.content);
		}
		return m;
	}
	async _decMsg(msg) {
		if (!msg) return msg;
		if (typeof msg.content === 'string') {
			msg.content = await dataCrypto.dec(msg.content);
		}
		// 旧缓存里的绝对域名URL改写为相对路径(纯IP应急通道可用)
		return rewriteOriginUrls(msg);
	}


	async loadAllConversations() {
		const list = await this.db.conversations.toArray();
		return Promise.all(list.map(c => this._decConv(c)));
	}

	async deleteConversationByKey(convKey) {
		try {
			return await this.db.transaction('rw', this.db.conversations, this.db.messages, async () => {
				await this.db.conversations.delete(convKey);
				await this.db.messages.where("convKey").equals(convKey).delete();
			});
		} catch (e) {
			console.error('删除会话失败:', e);
			throw e;
		}
	}

	// 远程清理：清空本地全部会话与消息缓存（保留好友/群/配置）
	async clearChatData() {
		return this.db.transaction('rw', this.db.conversations, this.db.messages, async () => {
			await this.db.conversations.clear();
			await this.db.messages.clear();
		});
	}

	async findConversationByKey(key) {
		const conv = await this.db.conversations.get(key);
		return this._decConv(conv);
	}

	async saveConversation(conversation) {
		return this.db.conversations.put(await this._encConv(conversation));
	}

	async saveConversationAndMessage(conversations, messages) {
		const encConvs = await Promise.all(conversations.map(c => this._encConv(c)));
		const encMsgs = await Promise.all(messages.map(m => this._encMsg(m)));
		return this.db.transaction('rw', this.db.conversations, this.db.messages, async () => {
			await this.db.conversations.bulkPut(encConvs);
			await this.db.messages.bulkPut(encMsgs);
		})
	}

	/**
	 * 向上加载某个会话的历史消息
	 * @param {string} convKey   会话key
	 * @param {number} minSeqNo    最小消息序号
	 * @param {object} maxSeqNo  最大消息序号
	 */
	async findPageMessage(convKey, minSeqNo, maxSeqNo) {
		// 构建查询范围
		const lower = [convKey, minSeqNo, 0];
		const upper = [convKey, maxSeqNo, Infinity];
		const messages = await this.db.messages
			.where('[convKey+seqNo+sendTime]')
			.between(lower, upper, true, true) // 后面两个true是包含边界值
			.toArray();
		return Promise.all(messages.map(m => this._decMsg(m)));
	}

	async deleteMessageByLocalId(localId) {
		try {
			return await this.db.messages.update(localId, { deleted: true });
		} catch (e) {
			console.error('删除消息失败:', e);
			throw e;
		}
	}

	async deleteMessageByConvKey(convKey) {
		return await this.db.messages.where("convKey").equals(convKey).delete();
	}

	/**
	 * 清理本地缓存中超过指定时间戳的旧消息(由定时清理任务触发)
	 * @param {number} threshold 时间戳(毫秒),早于此值的消息将被删除
	 * @param {Array} excludeIds 收藏消息ID列表,这些消息不会被删除
	 */
	async cleanupOldMessages(threshold, excludeIds = []) {
		const excludeSet = new Set(excludeIds);
		// sendTime 在消息对象中是毫秒时间戳,使用 filter 筛选后批量删除
		const oldMsgs = await this.db.messages
			.filter(m => {
				const t = m.sendTime;
				if (!t) return false;
				// 兼容 sendTime 为数字或字符串的情况
				const ts = typeof t === 'number' ? t : new Date(t).getTime();
				if (ts >= threshold) return false; // 未超期,不删
				if (excludeSet.has(m.id)) return false; // 收藏消息,不删
				return true;
			})
			.primaryKeys();
		if (oldMsgs.length > 0) {
			await this.db.messages.bulkDelete(oldMsgs);
			console.log(`[IndexedDB] 清理旧消息 ${oldMsgs.length} 条,排除收藏 ${excludeIds.length} 条,阈值: ${new Date(threshold).toLocaleString()}`);
		}
	}

	async saveMessage(message) {
		return await this.db.messages.put(await this._encMsg(message));
	}

	async findMessageById(convKey, messageId) {
		const msg = await this.db.messages
			.where('convKey')
			.equals(convKey)
			.filter(m => m.id == messageId)
			.first();
		return this._decMsg(msg);
	}

	async findMessageByLocalId(localId) {
		const msg = await this.db.messages.where('localId').equals(localId).first();
		return this._decMsg(msg);
	}

	async findMessageByConvKey(convKey) {
		const list = await this.db.messages.where("convKey").equals(convKey).toArray();
		return Promise.all(list.map(m => this._decMsg(m)));
	}

	async findAllFriends() {
		return await this.db.friends.toArray();
	}

	async saveFriends(friends) {
		await this.db.friends.bulkPut(friends);
	}

	async saveFriend(friend) {
		return await this.db.friends.put(friend);
	}

	async syncAllFriends(friends) {
		return this.db.transaction('rw', this.db.config, this.db.friends, async () => {
			const config = {
				key: 'lastSyncFriendTime',
				val: new Date().getTime()
			}
			await this.db.config.put(config);
			await this.db.friends.clear();
			await this.db.friends.bulkAdd(friends);
		});
	}

	async findLastSyncFriendsTime(friends) {
		const config = await this.db.config.where('key').equals('lastSyncFriendTime').first();
		return config ? config.val : 0;
	}

	async findAllGroups() {
		return await this.db.groups.toArray();
	}

	async saveGroups(groups) {
		await this.db.groups.bulkPut(groups);
	}

	async saveGroup(group) {
		return await this.db.groups.put(group);
	}

	async syncAllGroups(groups) {
		return this.db.transaction('rw', this.db.config, this.db.groups, async () => {
			const config = {
				key: 'lastSyncGroupTime',
				val: new Date().getTime()
			}
			await this.db.config.put(config);
			await this.db.groups.clear();
			await this.db.groups.bulkAdd(groups);
		});
	}

	async findLastSyncGroupsTime(groups) {
		const config = await this.db.config.where('key').equals('lastSyncGroupTime').first();
		return config ? config.val : 0;
	}

	async findRecentEmojis(limit = RECENT_EMOJI_MAX) {
		const list = await this.db.recentEmojis
			.orderBy('usedAt')
			.reverse()
			.limit(limit)
			.toArray();
		return list.map(item => item.text);
	}

	async addRecentEmoji(text) {
		await this.db.recentEmojis.put({ text, usedAt: Date.now() });
		const count = await this.db.recentEmojis.count();
		if (count > RECENT_EMOJI_MAX) {
			const oldItems = await this.db.recentEmojis
				.orderBy('usedAt')
				.limit(count - RECENT_EMOJI_MAX)
				.toArray();
			await this.db.recentEmojis.bulkDelete(oldItems.map(item => item.text));
		}
	}
}


export default ImIndexedDB;
